import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import routes from '../../routes';
import { RootState } from '../../reducers/reducers';
import { getIdFromAppId, getIdFromOrgId, getIdFromReleaseId } from '../../helpers';
import {
    AppRouteComponentMatchParams,
    OrgRouteComponentMatchParams,
    ReleaseRouteComponentMatchParams,
} from '../../typescriptHelpers';
import { selectOrg } from '../../selectors/selectors';

export type RouteContextType = 'app' | 'org' | 'user' | 'unknown';

export interface NavigationContext {
    orgId: string | null;
    appId: string | null;
    releaseId: string | null;

    orgIdParam: string | undefined;
    appIdParam: string | undefined;
    releaseIdParam: string | undefined;

    routeContext: RouteContextType;
    hasOrgContext: boolean;
    hasAppContext: boolean;
    hasReleaseContext: boolean;

    isAppRoute: boolean;
    isOrgRoute: boolean;
    isUserRoute: boolean;
    isReleaseRoute: boolean;
    isAppSettingsRoute: boolean;
    isReleaseCalendarRoute: boolean;

    isOrgSettingsRoute: boolean;
    isBackArrowRoute: boolean;
}

export function useNavigationContext(): NavigationContext {
    const appMatch = useRouteMatch<AppRouteComponentMatchParams>(routes.app);
    const orgMatch = useRouteMatch<OrgRouteComponentMatchParams>(routes.org);
    const userMatch = useRouteMatch(routes.user);
    const releaseMatch = useRouteMatch<ReleaseRouteComponentMatchParams>(routes.release);
    const appSettingsMatch = useRouteMatch(routes.appSettings);
    const orgSettingsMatch = useRouteMatch(routes.orgSettings);
    const releaseCalendarMatch = useRouteMatch(routes.appReleaseCalendar);

    const orgIdParam = appMatch?.params?.orgIdParam || orgMatch?.params?.orgIdParam;
    const appIdParam = appMatch?.params?.appIdParam;
    const releaseIdParam = releaseMatch?.params?.releaseIdParam;

    const orgId = useSelector((state: RootState) => {
        if (orgIdParam) {
            return getIdFromOrgId(orgIdParam, state);
        }
        return selectOrg(state)?.id || null;
    });

    const appId = useSelector((state: RootState) => {
        return orgIdParam && appIdParam ? getIdFromAppId(orgIdParam, appIdParam, state) : null;
    });

    const releaseId = useSelector((state: RootState) => {
        return releaseIdParam && orgId && appId ? getIdFromReleaseId(orgId, appId, releaseIdParam, state) : null;
    });

    const isAppRoute = !!appMatch;
    const isOrgRoute = !!orgMatch && !appMatch;
    const isUserRoute = !!userMatch;
    const isReleaseRoute = !!releaseMatch;
    const isAppSettingsRoute = !!appSettingsMatch;
    const isOrgSettingsRoute = !!orgSettingsMatch;
    const isReleaseCalendarRoute = !!releaseCalendarMatch;

    const isBackArrowRoute = isReleaseRoute || isAppSettingsRoute || isReleaseCalendarRoute;

    let routeContext: RouteContextType = 'unknown';
    if (isAppRoute) {
        routeContext = 'app';
    } else if (isOrgRoute) {
        routeContext = 'org';
    } else if (isUserRoute) {
        routeContext = 'user';
    }

    const hasOrgContext = !!orgId;
    const hasAppContext = !!orgId && !!appId;
    const hasReleaseContext = !!orgId && !!appId && !!releaseId;

    return {
        // Entity IDs
        orgId,
        appId,
        releaseId,

        // Route params
        orgIdParam,
        appIdParam,
        releaseIdParam,

        // Context type
        routeContext,
        hasOrgContext,
        hasAppContext,
        hasReleaseContext,

        // Specific route checks
        isAppRoute,
        isOrgRoute,
        isUserRoute,
        isReleaseRoute,
        isAppSettingsRoute,
        isOrgSettingsRoute,
        isReleaseCalendarRoute,
        isBackArrowRoute,
    };
}
